import { useEffect, useState } from "react";
import type { Chapter, Drama } from "@/types/drama";

type Props = {
  drama: Drama;
  chapter: Chapter;
};

export default function VideoPreview({ drama, chapter }: Props) {
  const [status, setStatus] = useState<"idle" | "playing" | "paused">("idle");

  useEffect(() => {
    setStatus("idle");
  }, [chapter.index]);

  const playing = status === "playing";

  return (
    <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-100">
      <div className="relative aspect-video w-full overflow-hidden bg-background-50">
        <img
          src={drama.cover}
          alt={`${drama.title} 预览区背景`}
          title={`${drama.title} 剧情预览演示`}
          className="h-full w-full scale-110 object-cover object-top opacity-35 blur-sm"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-background-50/40"></div>

        <span className="absolute left-3 top-3 flex items-center gap-1.5 rounded-full bg-background-50/75 px-2.5 py-1 font-label text-[10px] tracking-[0.14em] text-accent-500">
          <span className="h-1.5 w-1.5 rounded-full bg-accent-500"></span>
          预览片段 · 仅供界面演示
        </span>

        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          {status === "idle" ? (
            <>
              <button
                type="button"
                onClick={() => setStatus("playing")}
                aria-label="播放演示片段"
                className="group flex h-16 w-16 cursor-pointer items-center justify-center rounded-full border border-accent-500/50 bg-background-50/60 text-accent-500 transition-colors hover:border-accent-500 hover:bg-accent-500 hover:text-background-50"
              >
                <i className="ri-play-fill text-2xl"></i>
              </button>
              <p className="mt-4 font-heading text-base text-foreground-950 md:text-lg">
                第 {chapter.index} 集 · {chapter.title}
              </p>
              <p className="mt-2 max-w-md font-label text-[11px] leading-relaxed text-foreground-500">
                本站不提供完整剧集播放，此区域为前端播放器界面演示
              </p>
            </>
          ) : (
            <div className="w-full max-w-md">
              <div className="flex items-center justify-center gap-3">
                <span className="font-label text-[11px] tracking-[0.2em] text-accent-500">
                  {playing ? "演示播放中" : "已暂停"}
                </span>
                <span className="h-3 w-px bg-foreground-800/30"></span>
                <span className="font-label text-[11px] text-foreground-500">无真实片源</span>
              </div>
              <div className="mt-4 h-1 w-full overflow-hidden rounded-full bg-foreground-950/20">
                <div
                  className="h-full rounded-full bg-primary-500"
                  style={{
                    width: playing ? "42%" : "18%",
                    transition: playing ? "width 12s linear" : "width 0.4s ease-out",
                  }}
                ></div>
              </div>
              <div className="mt-2 flex items-center justify-between font-label text-[10px] text-foreground-500">
                <span>{playing ? "00:12" : "00:05"} / 00:30</span>
                <span>演示片段</span>
              </div>
              <p className="mt-4 rounded-md border border-background-300/40 bg-background-50/70 px-3 py-2.5 text-xs leading-relaxed text-foreground-600">
                当前为前端演示区域，暂未接入正式片源，因此不会有真实画面。你可以继续浏览章节简介与角色分析。
              </p>
              <div className="mt-4 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setStatus(playing ? "paused" : "playing")}
                  className="flex cursor-pointer items-center gap-1.5 rounded-full border border-accent-500/50 px-3.5 py-1.5 font-label text-[11px] text-accent-500 transition-colors hover:bg-accent-500 hover:text-background-50"
                >
                  <i className={`${playing ? "ri-pause-line" : "ri-play-line"} text-xs`}></i>
                  {playing ? "暂停" : "继续"}
                </button>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="flex cursor-pointer items-center gap-1.5 rounded-full border border-background-300/50 px-3.5 py-1.5 font-label text-[11px] text-foreground-600 transition-colors hover:border-foreground-600"
                >
                  <i className="ri-restart-line text-xs"></i>
                  重置
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 p-4 md:flex-row md:items-start md:justify-between md:gap-6">
        <div className="min-w-0">
          <p className="font-heading text-sm font-semibold text-foreground-950">
            第 {chapter.index} 集 · {chapter.title}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-foreground-600">{chapter.summary}</p>
        </div>
        <span className="shrink-0 rounded-full bg-background-200/70 px-2.5 py-1 font-label text-[10px] text-foreground-600">
          章节预览
        </span>
      </div>
    </div>
  );
}